import React, { useEffect, useMemo, useState } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { Box, Typography, MenuItem, Select, FormControl, InputLabel, CircularProgress, Stack, Skeleton } from "@mui/material";
import { dashboardService } from "../services/dashboardService";
import { userServices } from "../services/userServices";

// Convert "HH:mm" string to total minutes
const timeToMinutes = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
};

const monthLabels = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const generateMonthYearOptions = (monthsBack = 6) => {
    const now = new Date();
    const opts = [];
    for (let i = 0; i < monthsBack; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const y = d.getFullYear();
        const m = d.getMonth() + 1; // 1..12
        const label = `${monthLabels[d.getMonth()]} ${y}`;
        const value = `${y}-${String(m).padStart(2, "0")}`; // "2025-02"
        opts.push({ value, label, month: m, year: y });
    }
    return opts;
};

const MonthlyBreakBarChart = ({ roles, currentUserID }) => {
    const isLeaderOrAdmin = roles?.includes("SUPER_ADMIN") || roles?.includes("ADMIN") || roles?.includes("SUB_ADMIN") || roles?.includes("MANAGE_JOBS");
    const isAdmin = roles?.includes("ADMIN") || roles?.includes("SUPER_ADMIN");
    const isAdminOrSubAdmin = roles?.includes("SUPER_ADMIN") || roles?.includes("ADMIN") || roles?.includes("SUB_ADMIN");
    // const currentUserID = localStorage.getItem('userId');

    const monthOptions = useMemo(() => generateMonthYearOptions(6), []);

    // default selection = current month (first item)
    const defaultMonthValue = monthOptions.length ? monthOptions[0].value : (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    })();

    const [selectedMonthYear, setSelectedMonthYear] = useState(defaultMonthValue);
    const [selectedUser, setSelectedUser] = useState(null);
    const [teamUsers, setTeamUsers] = useState([]);
    const [breakData, setBreakData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedMonth, selectedYear] = useMemo(() => {
        const parts = (selectedMonthYear || defaultMonthValue).split("-");
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        return [m, y];
    }, [selectedMonthYear, defaultMonthValue]);

    useEffect(() => {

        if (!isLeaderOrAdmin) {
            setSelectedUser(currentUserID);
        } else {
            const fetchTeamUsers = async () => {
                try {
                    const users = await userServices.getUsersByRoleName();
                    setTeamUsers(users);
                    if (isAdminOrSubAdmin && users.length > 0) {
                        setSelectedUser(users[0].id);
                    } else {
                        setSelectedUser(currentUserID);
                    }
                } catch (err) {
                    console.error("Failed to fetch team users", err);
                }
            };
            fetchTeamUsers();
        }
    }, [isLeaderOrAdmin, isAdminOrSubAdmin, currentUserID]);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedUser) return;
            try {
                setLoading(true);
                const data = await dashboardService.getMonthlyBreaksForDashboard(selectedUser, selectedMonth, selectedYear);
                const sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));
                setBreakData(sortedData);

            } catch (err) {
                console.error("Failed to fetch monthly breaks", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedUser, selectedMonth, selectedYear]);

    const xLabels = breakData.map((entry) => {
        const date = new Date(entry.date);
        return `${date.getDate()}/${date.getMonth() + 1}`;
    });

    const under90Series = [];
    const exactly90Series = [];
    const over90Series = [];

    breakData.forEach((entry) => {
        const duration = timeToMinutes(entry.duration);
        if (duration < 90) {
            under90Series.push(duration);
            exactly90Series.push(0);
            over90Series.push(0);
        } else if (duration === 90) {
            under90Series.push(0);
            exactly90Series.push(duration);
            over90Series.push(0);
        } else {
            under90Series.push(0);
            exactly90Series.push(0);
            over90Series.push(duration);
        }
    });

    const seriesValueFormatter = (value, { dataIndex }) => {
        if (value === 0 || value == null) return null;
        return breakData[dataIndex]?.duration ?? `${value} min`;
    };

    return (
        <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
                {isLeaderOrAdmin && teamUsers.length > 0 && (
                    <FormControl sx={{ flex: 1, minWidth: 120 }}>
                        <InputLabel id="select-user-label">Select User</InputLabel>
                        <Select
                            labelId="select-user-label"
                            value={(Array.isArray(teamUsers) && teamUsers.some((u) => String(u.id) === String(selectedUser))) ? selectedUser : ''}
                            label="Select User"
                            onChange={(e) => setSelectedUser(e.target.value)}
                            size="small"
                        >
                            {teamUsers.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {u.firstName} {u.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {/* {isLeaderOrAdmin && teamUsers.length > 0 && ( */}
                <FormControl sx={{ flex: 1, minWidth: 120 }}>
                    <InputLabel id="month-year-select-label">Select Month</InputLabel>
                    <Select
                        labelId="month-year-select-label"
                        value={(Array.isArray(monthOptions) && monthOptions.some((opt) => opt.value === selectedMonthYear)) ? selectedMonthYear : ''}
                        label="Month & Year"
                        onChange={(e) => setSelectedMonthYear(e.target.value)}
                        size="small"
                    >
                        {monthOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {/* )} */}
            </Stack>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 6,
                        gap: 2,
                    }}
                >
                    <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 1 }} />
                </Box>
            ) : breakData.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ height: 250, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    No break data available
                </Typography>
            ) : (
                <Box className="chart-pie-box">
                    <BarChart
                        series={[
                            { data: under90Series, color: "#66bb6a", stack: "a", valueFormatter: seriesValueFormatter },
                            { data: exactly90Series, color: "#42a5f5", stack: "a", valueFormatter: seriesValueFormatter },
                            { data: over90Series, color: "#ef5350", stack: "a", valueFormatter: seriesValueFormatter },
                        ]}
                        xAxis={[{ data: xLabels, scaleType: "band", label: "Days of Month", labelStyle: { fontSize: 12 } }]}
                        yAxis={[{ label: "Break Duration (minutes)", labelStyle: { fontSize: 12 } }]}
                        slotProps={{ bar: { rx: 4 }, tooltip: { trigger: "axis" } }}
                        margin={{ left: 70, right: 30, top: 30, bottom: 70 }}
                        grid={{ vertical: true, horizontal: true }}
                        sx={{ width: "100%", height: "100%" }}
                    />
                </Box>
            )}

            {/* Legend */}
            {breakData.length > 0 && (
                <Box className="break-legend-container">
                    <Box className="break-legend-item">
                        <Box className="break-color-box break-less" />
                        <Typography variant="body2">Less than 90 min</Typography>
                    </Box>
                    <Box className="break-legend-item">
                        <Box className="break-color-box break-exact" />
                        <Typography variant="body2">Exactly 90 min</Typography>
                    </Box>
                    <Box className="break-legend-item">
                        <Box className="break-color-box break-more" />
                        <Typography variant="body2">More than 90 min</Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default MonthlyBreakBarChart;